import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import { api } from '../lib/api';
import type { Query } from '../lib/api';
import { t } from '../lib/i18n';
import { errorMessage } from './ErrorPanel';

export type ExportFormat = 'csv' | 'xlsx';

/** Saves a Blob through a temporary link (the browser's normal download). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Downloads a list as CSV or Excel. It calls `GET /api/v1{path}?format=csv|xlsx` with the list's current
 * filters in `query`, so the file matches what is on screen. The server checks the permission.
 */
export function ExportButton({
  path,
  query,
  formats = ['csv', 'xlsx'],
  fileName = 'export',
  disabled = false,
}: {
  path: string;
  query?: Query;
  formats?: ExportFormat[];
  /** Used when the server sends no file name. The extension is added. */
  fileName?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const run = async (format: ExportFormat) => {
    setOpen(false);
    setBusy(true);
    setError(null);
    try {
      const { blob, filename } = await api.download(path, { query: { ...query, format } });
      saveBlob(blob, filename ?? `${fileName}.${format}`);
    } catch (e) {
      setError(errorMessage(e) || t('widget.export.failed'));
    } finally {
      setBusy(false);
    }
  };

  const single = formats.length === 1 ? formats[0] : undefined;
  const btn =
    'inline-flex min-h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50';
  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        className={btn}
        disabled={disabled || busy}
        aria-expanded={single ? undefined : open}
        aria-controls={single ? undefined : menuId}
        onClick={() => (single ? void run(single) : setOpen((o) => !o))}
      >
        <Download aria-hidden="true" className="h-4 w-4" />
        {busy ? t('widget.export.working') : t('widget.export.label')}
        {single ? null : <ChevronDown aria-hidden="true" className="h-4 w-4" />}
      </button>
      {open ? (
        <ul id={menuId} className="absolute right-0 z-30 mt-1 w-44 rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
          {formats.map((f) => (
            <li key={f}>
              <button
                type="button"
                className="flex min-h-9 w-full items-center rounded-md px-3 text-left text-sm text-slate-800 hover:bg-slate-50"
                onClick={() => void run(f)}
              >
                {t(f === 'csv' ? 'widget.export.csv' : 'widget.export.xlsx')}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {error ? (
        <p role="alert" className="mt-1 text-sm text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
